import { rtlGate } from "@/lib/rtlSafety";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveData } from "@/context/LiveDataContext";
import { useGamepad } from "@/hooks/useGamepad";
import { ACTION_LABEL } from "@/lib/manualMapping";
import CockpitVideoHUD from "@/components/CockpitVideoHUD";
import { useRaspberryHealth } from "@/hooks/useRaspberryHealth";
import { EmbeddedHealthPanel, RaspberryBadge } from "@/components/RaspberryHealth";
import { NetworkStatusWidget } from "@/components/NetworkStatusWidget";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { buildCockpitState } from "@/lib/cockpitState";
import { VideoHealthPanel, VideoProfileSlider, CockpitVideoMetrics } from "@/components/VideoHealth";
import { useVideoFeed, VIDEO_UI, CAUSE_LABEL } from "@/hooks/useVideoFeed";
import { useVideoProfile } from "@/hooks/useVideoProfile";
import { useCameraZoom } from "@/hooks/useCameraZoom";
import { DroneJoystick, DroneAux, CameraJoystick, Draggable } from "@/components/CockpitControls";
import ControllerConfig from "@/components/ControllerConfig";
import CockpitMiniMap from "@/components/CockpitMiniMap";
import OperatorCockpitPanel from "@/components/OperatorCockpitPanel";
import OperatorFlightSessionPanel from "@/components/OperatorFlightSessionPanel";
import FlightControllerPanel from "@/components/FlightControllerPanel";
import MapView from "@/components/MapView";
import { Field } from "@/components/common";
import { fmtTime, num } from "@/lib/format";
import { toast } from "sonner";
import { useRealOffboardControl } from "@/hooks/useRealOffboardControl";
import { usePilotInput } from "@/hooks/usePilotInput";
import { useCurrentFlightSession } from "@/hooks/useCurrentFlightSession";
import { homePositionView } from "@/lib/pilotageOperator";
import { offboardCapabilityBlocked, isTelemetryStale } from "@/lib/commandGating";
import { resolvePilotDrone, getLastPilotDrone, setLastPilotDrone, droneNetworkState, pilotNavigationLockReason, LOCK_REASON_LABEL } from "@/lib/pilotageNavigation";
import { REAL_DEFAULT_ALT, isRealDirection, buildRealHud, buildRealMapDrone, validateTakeoffAltitude } from "@/lib/offboardControl";
import { canEnterVR } from "@/webxr/xrSupport";
import { RotateCcw, Settings2, ArrowLeft, ShieldAlert, ScrollText, Map as MapIcon, Maximize2, Minimize2, Power, LayoutGrid, Wifi, Radio, Cctv, Gauge, Loader2, VideoOff, Play, CheckCircle2, XCircle, HelpCircle, Clock, ChevronDown, Route, Glasses } from "lucide-react";

// UNIFIED.2 — labels lisibles des raisons de précheck (diagnostic).
const PRECHECK_LABEL = {
  drone_link_lost: "Liaison drone perdue",
  mavlink_disconnected: "MAVLink déconnecté",
  telemetry_stale: "Télémétrie périmée",
  not_disarmed: "Drone armé",
  not_on_ground: "Drone pas au sol",
  gps_fix_lt_3: "GPS : fix 3D requis",
  insufficient_satellites: "Satellites insuffisants",
  hdop_too_high: "Précision GPS insuffisante",
  battery_too_low: "Batterie insuffisante",
  global_position_invalid: "Position globale invalide",
  local_position_invalid: "Position locale invalide",
};

// Ligne d'état du panneau « Préparation au vol ». ok: true=✓ OK, false=✕ BLOQUÉ, null/undefined=? INCONNU (bloquant).
const PfRow = ({ label, ok, detail, testid }) => {
  const st = ok === true ? { Icon: CheckCircle2, color: "#22C55E", txt: detail || "OK" }
    : ok === false ? { Icon: XCircle, color: "#EF4444", txt: detail || "BLOQUÉ" }
    : ok === "wait" ? { Icon: Clock, color: "#A3A3A3", txt: detail || "EN ATTENTE" }
    : { Icon: HelpCircle, color: "#F59E0B", txt: detail || "INCONNU" };
  return (
    <div className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
      <span className="text-[9px] uppercase tracking-wider text-white/50">{label}</span>
      <span data-testid={testid} className="flex items-center gap-1 font-mono text-[10px]" style={{ color: st.color }}>
        <st.Icon size={11} /> {st.txt}
      </span>
    </div>
  );
};
const pfCheck = (v) => (v === true ? true : v === false ? false : null);

const VBadge = ({ on, icon: Icon, label, value, testid }) => (
  <span data-testid={testid} className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider"
    style={{ borderColor: on ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.12)", color: on ? "#22C55E" : "#525252", background: on ? "rgba(34,197,94,0.08)" : "transparent" }}>
    <Icon size={11} /> {label}{value != null ? <span className="text-white/80">· {value}</span> : null}
  </span>
);

// Pilotage manuel — RÉEL UNIQUEMENT. Source canonique du contrôle : useRealOffboardControl.
// Aucun mode simulation, aucun toggle, aucune state machine secondaire.
export default function ManualControl() {
  const { drone_id: droneId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { drones } = useLiveData();
  const isAdmin = user?.role === "admin";

  const [profile, setProfile] = useState(null);
  const [baseCfg, setBaseCfg] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [isFs, setIsFs] = useState(false);
  const [realAlt, setRealAlt] = useState(REAL_DEFAULT_ALT);
  const [minimapVisible, setMinimapVisible] = useState(() => localStorage.getItem("manual_control_minimap_visible") === "1");
  const [layout, setLayout] = useState(() => { try { return JSON.parse(localStorage.getItem(`cockpit_layout_v2_${droneId}`) || "{}"); } catch { return {}; } });
  const setBlock = useCallback((key, p) => setLayout((l) => { const n = { ...l, [key]: p }; try { localStorage.setItem(`cockpit_layout_v2_${droneId}`, JSON.stringify(n)); } catch { /* ignore */ } return n; }), [droneId]);
  const resetLayout = useCallback(() => { setLayout({}); try { localStorage.removeItem(`cockpit_layout_v2_${droneId}`); } catch { /* ignore */ } toast.success("Disposition des modules réinitialisée"); }, [droneId]);
  const [zoomFlash, setZoomFlash] = useState(false);
  const prevZoomRef = useRef(1);

  const { state: padState, padRef } = useGamepad();
  const vf = useVideoFeed(droneId, { autoStart: true, autoReconnect: true });
  const rpi = useRaspberryHealth(droneId, { interval: 9000 });
  const vprofile = useVideoProfile(droneId, { onReconnect: () => vf.connect() });
  const camZoom = useCameraZoom(droneId, { enabled: !!vf.cfg?.enabled });
  useEffect(() => { if (camZoom.error) toast.error(camZoom.error); }, [camZoom.error]);
  // Indicateur de zoom transitoire (retour visuel immédiat au centre-bas de la vidéo).
  useEffect(() => {
    if (!vf.cfg?.enabled) return;
    if (camZoom.level === prevZoomRef.current) return;
    prevZoomRef.current = camZoom.level;
    setZoomFlash(true);
    const t = setTimeout(() => setZoomFlash(false), 1200);
    return () => clearTimeout(t);
  }, [camZoom.level, vf.cfg?.enabled]);
  const videoEnabled = !!vf.cfg?.enabled;
  const videoActive = !!vf.playMode;
  const vui = VIDEO_UI[vf.uiState] || VIDEO_UI.unavailable;
  const cockpitRef = useRef(null);

  const drone = drones.find((d) => d.drone_id === droneId);

  // UNIFIED.6 — Base CONFIGURÉE (CUSTOM) UNIQUEMENT. ardupilot_home_* n'est PLUS utilisé comme HOME
  // live (le vrai HOME vient du Raspberry/PX4 via real.status.home_position).
  const configuredBasePoint = useMemo(() => {
    if (baseCfg?.base_mode === "CUSTOM_BASE" && baseCfg?.base_lat != null)
      return { lat: baseCfg.base_lat, lng: baseCfg.base_lng, label: baseCfg.base_address, title: "Base configurée" };
    return null;
  }, [baseCfg]);

  // Chaîne OFFBOARD réelle — unique source de pilotage. Contrôle réservé aux admins (politique inchangée).
  const real = useRealOffboardControl(droneId, { enabled: isAdmin, flightController: drone?.flight_controller });

  // COCKPIT.FINAL.7 — abstraction d'entrée pilote commune (Keyboard/Touch/Gamepad -> real.*).
  // RTL.SAFETY.1-R2 — clavier/manette/tactile/XR passent par le même gate RTL que les boutons.
  const rtlG = rtlGate(drone);
  const pilotReal = { ...real, rtlReal: () => (rtlG.allowed ? real.rtlReal() : toast.error(`RTL indisponible : ${rtlG.reason}`)) };
  const pilot = usePilotInput({ real: pilotReal, padRef, profile, enabled: isAdmin });

  // UNIFIED.6 — HOME PX4 live (validé par le helper pur). mappable=false => aucun marker/lien HOME.
  const home = homePositionView(real.status);
  const homePoint = home.mappable
    ? { lat: home.lat, lng: home.lon, altAmsl: home.altAmsl, ageS: home.ageS }
    : null;

  // ---- data loading ----
  useEffect(() => {
    api.get("/controller-profiles/default").then(({ data }) => setProfile(data.profile)).catch(() => {});
    api.get(`/drones/${droneId}/config`).then(({ data }) => setBaseCfg(data.config)).catch(() => {});
  }, [droneId]);

  const loadEvents = useCallback(() => {
    api.get(`/drones/${droneId}/manual-control/events`, { params: { limit: 40 } })
      .then(({ data }) => setEvents(data.events || [])).catch(() => {});
  }, [droneId]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const logEvent = useCallback((action, source, value = null) => {
    api.post(`/drones/${droneId}/manual-control/event`, { mode: "real", source, action, value }).catch(() => {});
  }, [droneId]);

  // ---- joystick/boutons translation : routés via l'abstraction d'entrée pilote (COCKPIT.FINAL.7) ----
  const onJoyHold = useCallback((dir, on) => {
    if (!isRealDirection(dir)) return;
    pilot.touchHold(dir, on);
  }, [pilot]);

  // ---- clavier : zoom caméra uniquement (le pilotage clavier passe par usePilotInput) ----
  useEffect(() => {
    const onDown = (e) => {
      if (e.repeat) return;
      if (e.code === "Equal" && videoEnabled) camZoom.zoomIn();
      else if (e.code === "Minus" && videoEnabled) camZoom.zoomOut();
    };
    window.addEventListener("keydown", onDown);
    return () => window.removeEventListener("keydown", onDown);
  }, [videoEnabled, camZoom]);

  // ---- fullscreen ----
  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) await cockpitRef.current?.requestFullscreen();
      else await document.exitFullscreen();
    } catch { toast.error("Plein écran non supporté par ce navigateur"); }
  }, []);
  useEffect(() => {
    const onFs = () => {
      const fs = !!document.fullscreenElement;
      setIsFs(fs);
      logEvent(fs ? "fullscreen_enter" : "fullscreen_exit", "ui");
      setTimeout(loadEvents, 300);
    };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [logEvent, loadEvents]);

  // ---- minimap preferences + handlers ----
  useEffect(() => { localStorage.setItem("manual_control_minimap_visible", minimapVisible ? "1" : "0"); }, [minimapVisible]);
  const onMinimapEvent = useCallback((action) => { logEvent(action, "ui"); setTimeout(loadEvents, 300); }, [logEvent, loadEvents]);
  const showMinimap = () => { setMinimapVisible(true); onMinimapEvent("minimap_show"); };
  const hideMinimap = () => { setMinimapVisible(false); onMinimapEvent("minimap_hide"); };

  // HUD/carte RÉELS depuis la télémétrie LIVE + statut autoritaire.
  const realHud = buildRealHud(real.status, real.safety, drone);
  const mapDrone = buildRealMapDrone(droneId, drone);
  const recenter = mapDrone.positionKnown ? [mapDrone.lat, mapDrone.lng] : undefined;
  // UNIFIED.2 — état de connexion/relais pour le bouton DÉMARRER et le panneau préparation au vol.
  const fcBlocked = offboardCapabilityBlocked(drone?.flight_controller);
  const gwForwarded = real.transport?.gateway_forwarded === true;
  const controlOpen = real.conn === "open";
  const controlConnected = controlOpen && !!real.sessionId && gwForwarded;
  // UNIFIED.3 — politique TAKEOFF effective + validation altitude explicite (aucun clamp silencieux).
  const takeoffPolicy = real.takeoffPolicy;
  const altCheck = validateTakeoffAltitude(realAlt, takeoffPolicy);
  const pl = real.profileLimits;
  // UNIFIED.5 — « PRÊT À LANCER » (séquence PREPARE→ARM→TAKEOFF demandable), jamais « prêt à armer ».
  const readyForLaunchSequence = real.canStartFlight === true && altCheck.valid === true;
  // UNIFIED.6 — archive Flight Session Backend EXACTE, corrélée au lease (droneId + SID). GET exact, jamais "latest".
  const backendSession = useCurrentFlightSession(droneId, real.sessionId, !!real.sessionId);

  // COCKPIT.FINAL.6 — état réseau CENTRALISÉ (un seul polling, injecté partout via netData).
  const net = useNetworkStatus(droneId, { interval: 12000, enabled: !!droneId });

  // COCKPIT.FINAL.6 — modèle d'état cockpit READ-ONLY commun (socle React DOM / futur WebXR).
  const cockpit = buildCockpitState({
    droneId, drone, real, telemetryFresh: !isTelemetryStale(drone?.last_seen),
    gatewayForwarded: gwForwarded, video: vf, raspberryHealth: rpi, network: net.net, backendSession,
    pilotInput: pilot.input,
  });

  // UNIFIED.4 — verrou de navigation cockpit (empêche de démonter une session de vol en cours).
  const navLockReason = pilotNavigationLockReason({
    sessionId: real.sessionId, conn: real.conn, safety: real.safety,
    status: real.status, phase: real.phase, orch: real.orch, criticalAction: real.criticalAction,
  });
  const navLocked = !!navLockReason;
  const changeDrone = useCallback((id) => {
    if (navLocked || !id || id === droneId) return;
    setLastPilotDrone(id);
    navigate(`/manual-control/${id}`);  // aucun auto-connect : le nouveau cockpit revient sur DÉMARRER
  }, [navLocked, droneId, navigate]);
  const goMissions = useCallback(() => { if (!navLocked) navigate(`/missions?drone=${encodeURIComponent(droneId)}`); }, [navLocked, droneId, navigate]);
  const goBack = useCallback(() => { if (!navLocked) navigate(`/drones?id=${droneId}`); }, [navLocked, droneId, navigate]);

  // WEBXR.2 — accès cockpit VR (Quest 3). Désactivé si contrôle actif / lease / action critique / navLock.
  const vrDisabled = !canEnterVR({ navLocked, conn: real.conn, sessionId: real.sessionId, criticalAction: real.criticalAction });
  const goVR = useCallback(() => { if (!vrDisabled) navigate(`/vr/${encodeURIComponent(droneId)}`); }, [vrDisabled, droneId, navigate]);

  // Fallback route invalide + mémorisation du dernier drone (après flotte chargée).
  useEffect(() => {
    if (!drones.length) return;
    if (!drones.some((d) => d.drone_id === droneId)) {
      const fb = resolvePilotDrone(drones, getLastPilotDrone());
      if (fb && fb !== droneId) navigate(`/manual-control/${fb}`, { replace: true });
      return;
    }
    setLastPilotDrone(droneId);
  }, [drones, droneId, navigate]);

  return (
    <div className="h-full flex flex-col overflow-x-hidden">
      {/* Header */}
      <div className="shrink-0 px-3 sm:px-5 py-2.5 sm:py-3 border-b border-white/10 flex items-center gap-2 sm:gap-3 flex-wrap">
        <button data-testid="back-btn" disabled={navLocked} onClick={goBack} className="w-8 h-8 flex items-center justify-center border border-white/15 rounded-sm hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"><ArrowLeft size={15} /></button>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="font-chivo text-lg sm:text-xl font-black tracking-tighter uppercase leading-none">Pilotage manuel</h1>
          <div className="relative flex items-center">
            <select data-testid="pilot-drone-selector" value={droneId} disabled={navLocked}
              onChange={(e) => changeDrone(e.target.value)}
              className="appearance-none bg-black/50 border border-white/20 rounded-sm pl-2.5 pr-7 py-1.5 font-mono text-[11px] uppercase tracking-wider text-white hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed">
              {drones.map((d) => (
                <option key={d.drone_id} value={d.drone_id}>{d.drone_id} · {droneNetworkState(d)}</option>
              ))}
            </select>
            <ChevronDown size={13} className="absolute right-2 pointer-events-none text-white/50" />
          </div>
          {navLocked && <span data-testid="pilot-nav-lock" className="font-mono text-[9px] text-red-400">{LOCK_REASON_LABEL[navLockReason] || "DRONE VERROUILLÉ"}</span>}
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <button data-testid="pilot-missions-btn" disabled={navLocked} onClick={goMissions} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-2.5 sm:px-3 py-2 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"><Route size={13} /> Missions</button>
          <button data-testid="pilot-vr-btn" disabled={vrDisabled} onClick={goVR} title="Cockpit VR Quest 3" className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-2.5 sm:px-3 py-2 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"><Glasses size={13} /> <span className="hidden sm:inline">VR</span> Quest</button>
          <button data-testid="open-config" onClick={async () => { if (document.fullscreenElement) { try { await document.exitFullscreen(); } catch { /* ignore */ } } setConfigOpen(true); }} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-2.5 sm:px-3 py-2 hover:bg-white/10"><Settings2 size={13} /> <span className="hidden sm:inline">Config</span> manette</button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-5 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        {/* Cockpit (fullscreen target) — la vidéo réelle est le visuel principal */}
        <div className="lg:col-span-2 min-w-0 space-y-4">
          <div ref={cockpitRef} data-testid="cockpit" className="relative w-full aspect-video min-h-[300px] sm:min-h-0 bg-black border border-white/10 rounded-sm overflow-hidden">
            <CockpitVideoHUD droneId={droneId} padConnected={padState.connected}
              realHud={realHud}
              videoActive={videoActive}
              videoNode={videoEnabled ? (
                <video ref={vf.videoRef} data-testid="cockpit-video" autoPlay muted playsInline className="w-full h-full object-cover bg-black" />
              ) : null}
              statusOverlay={videoEnabled && !videoActive ? (
                <div className="absolute inset-0 z-[15] flex items-center justify-center px-6 pointer-events-none" data-testid="cockpit-video-overlay">
                  <div className="pointer-events-auto flex flex-col items-center gap-2 bg-black/60 border rounded-md px-5 py-4 backdrop-blur-md text-center" style={{ borderColor: vui.color + "55" }}>
                    {vf.connecting || vf.service.pending
                      ? <Loader2 size={22} className="animate-spin text-[#3B82F6]" />
                      : <VideoOff size={22} style={{ color: vui.color }} />}
                    <span className="font-mono text-[11px] uppercase tracking-wider" style={{ color: vui.color }} data-testid="cockpit-video-state">{vui.label}</span>
                    {vf.diagnostic.cause && (
                      <div className="font-mono text-[9px] text-[#F59E0B] max-w-xs leading-relaxed" data-testid="cockpit-video-cause">{CAUSE_LABEL[vf.diagnostic.cause]}</div>
                    )}
                    {!(vf.connecting || vf.service.pending) && (
                      <div className="flex items-center gap-2">
                        <button data-testid="cockpit-video-activate" onClick={() => vf.controlService("start")}
                          className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider bg-[#22C55E] text-black rounded-full px-3 py-1 hover:bg-[#22C55E]/85"><Power size={11} /> Activer vidéo</button>
                        <button data-testid="cockpit-video-retry" onClick={vf.connect}
                          className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider border border-white/20 rounded-full px-3 py-1 hover:bg-white/10"><RotateCcw size={11} /> Réessayer</button>
                      </div>
                    )}
                  </div>
                </div>
              ) : null} />

            {videoEnabled && (
              <div className="absolute bottom-2 right-2 z-30 pointer-events-none">
                <CockpitVideoMetrics vf={vf} vp={vprofile} zoomLevel={camZoom.level} />
              </div>
            )}

            {/* Overlays */}
            <div className="absolute inset-0 z-10 pointer-events-none">
              {/* Top-center: safety banner + fullscreen/layout */}
              <div className="absolute top-2 sm:top-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 w-full sm:w-auto px-2 sm:px-0">
                <div data-testid="safety-banner" className="pointer-events-auto flex items-center gap-1.5 bg-black/55 border rounded-full px-3 py-1 backdrop-blur-md max-w-full" style={{ borderColor: "rgba(239,68,68,0.5)" }}>
                  <ShieldAlert size={11} className="shrink-0" style={{ color: "#EF4444" }} />
                  <span className="font-mono text-[9px] tracking-wider truncate text-[#EF4444]" data-testid="safety-banner-real"><span className="hidden sm:inline">{real.controlLimitsReady && pl ? `CONTRÔLE RÉEL · ${real.speedProfile.toUpperCase()} · XY ${pl.max_xy.toFixed(2)} m/s` : "CONTRÔLE RÉEL · LIMITES EN ATTENTE"}</span><span className="sm:hidden">CONTRÔLE RÉEL</span></span>
                </div>
                <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-1 bg-black/50 border border-white/15 rounded-2xl sm:rounded-full px-1.5 py-1 backdrop-blur-md max-w-full">
                  {real.conn === "connecting" || (controlOpen && !real.sessionId) ? (
                    <button data-testid="pilot-start-control" disabled
                      className="flex items-center gap-1 px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider text-white/50 bg-white/5 cursor-not-allowed">
                      <Loader2 size={11} className="animate-spin" /> Connexion…
                    </button>
                  ) : controlOpen && real.sessionId && !gwForwarded ? (
                    <button data-testid="pilot-start-control" disabled
                      className="flex items-center gap-1 px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider text-white/50 bg-white/5 cursor-not-allowed">
                      <Loader2 size={11} className="animate-spin" /> Connexion drone…
                    </button>
                  ) : controlConnected ? (
                    <span data-testid="pilot-start-control"
                      className="flex items-center gap-1 px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/40">
                      ● Contrôle connecté{real.sessionId ? ` · SID ${real.sessionId.slice(0, 8)}` : ""}
                    </span>
                  ) : (
                    <button data-testid="pilot-start-control" onClick={real.connect}
                      className="flex items-center gap-1 px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider text-black bg-[#22C55E] hover:bg-[#22C55E]/85">
                      <Play size={11} /> Démarrer
                    </button>
                  )}
                  <span className="w-px h-4 bg-white/15" />
                  {["slow", "normal", "rapid"].map((m) => (
                    <button key={m} data-testid={`speed-${m}`} onClick={() => real.setSpeedProfile(m)}
                      className={`px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider transition-colors ${real.speedProfile === m ? "bg-[#3B82F6] text-white" : "text-white/60 hover:text-white"}`}>
                      {m === "slow" ? "Lent" : m === "normal" ? "Normal" : "Rapide"}
                    </button>
                  ))}
                  <button data-testid="speed-down" onClick={real.speedDown} className="px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider text-white/70 hover:text-white">Vit −</button>
                  <button data-testid="speed-up" onClick={real.speedUp} className="px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider text-white/70 hover:text-white">Vit +</button>
                  <span className="w-px h-4 bg-white/15" />
                  <button data-testid="fullscreen-btn" onClick={toggleFullscreen}
                    className="flex items-center gap-1 px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider text-white/80 hover:text-white">
                    {isFs ? <Minimize2 size={11} /> : <Maximize2 size={11} />} {isFs ? "Quitter" : "Plein écran"}
                  </button>
                  <button data-testid="reset-layout-btn" onClick={resetLayout}
                    className="flex items-center gap-1 px-2 py-1 rounded-full font-mono text-[9px] uppercase tracking-wider text-white/80 hover:text-white">
                    <LayoutGrid size={11} /> Dispo
                  </button>
                </div>
                {/* Santé embarquée (compact) — visible en permanence */}
                <div className="pointer-events-auto flex items-center gap-1.5 flex-wrap justify-center">
                  <RaspberryBadge overall={rpi.health?.overall || "unknown"} testid="cockpit-rpi-badge" />
                  <NetworkStatusWidget droneId={droneId} variant="badge" netData={net.net} />
                </div>
              </div>

              {/* Overlay profils + infos vidéo (visible en plein écran) */}
              {videoEnabled && (
                <div className="absolute pointer-events-none" style={{ top: 92, left: 12, zIndex: 35 }} data-testid="cockpit-video-overlay-info">
                  <VideoProfileSlider vp={vprofile} />
                </div>
              )}

              {/* Indicateur de zoom transitoire */}
              {videoEnabled && zoomFlash && (
                <div data-testid="cockpit-zoom-indicator" className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{ bottom: "34%", zIndex: 40 }}>
                  <div className="bg-black/70 border border-[#2DD4BF]/50 rounded-full px-4 py-1.5 backdrop-blur-md font-mono text-base font-bold text-[#2DD4BF]">{(camZoom.level || 1).toFixed(1)}x</div>
                </div>
              )}

              {/* Mini-map overlay (visible in fullscreen) */}
              {minimapVisible ? (
                <CockpitMiniMap drone={mapDrone} trace={null} homePoint={homePoint} configuredBasePoint={configuredBasePoint} cockpitRef={cockpitRef}
                  onHide={hideMinimap} onEvent={onMinimapEvent} />
              ) : (
                <button data-testid="minimap-show" onClick={showMinimap}
                  className="pointer-events-auto absolute flex items-center gap-1 bg-black/55 border border-[#38BDF8]/55 text-[#38BDF8] rounded-md px-2.5 py-1.5 backdrop-blur-md font-mono text-[9px] uppercase tracking-wider hover:bg-black/70"
                  style={{ top: 88, right: 24, zIndex: 40 }}>
                  <MapIcon size={12} /> Afficher carte
                </button>
              )}

              {/* Bottom: joysticks déplaçables (disposition persistée par navigateur) */}
              <Draggable pos={layout.translation} defaultClass="left-3 bottom-3" boundsRef={cockpitRef} onChange={(p) => setBlock("translation", p)}>
                {(hp) => <DroneJoystick onHold={onJoyHold} active={real.activeDirs} disabled={!real.moveAllowed} handleProps={hp} />}
              </Draggable>
              <Draggable pos={layout.aux} defaultClass="left-1/2 -translate-x-1/2 bottom-3" boundsRef={cockpitRef} onChange={(p) => setBlock("aux", p)}>
                {(hp) => (
                  <DroneAux handleProps={hp} realMode onHold={onJoyHold} active={real.activeDirs}
                    moveDisabled={!real.moveAllowed}
                    altitude={realAlt} onAltitude={setRealAlt}
                    minAltitude={takeoffPolicy?.min} maxAltitude={takeoffPolicy?.max} altitudePolicyReady={!!takeoffPolicy}
                    onArmTakeoff={() => { if (validateTakeoffAltitude(realAlt, real.takeoffPolicy).valid) real.startFlight(realAlt); else toast.error("Altitude TAKEOFF hors politique autorisée"); }}
                    armDisabled={!real.canStartFlight || !altCheck.valid}
                    hoverDisabled={!real.moveAllowed}
                    landDisabled={!!real.criticalAction || real.conn !== "open" || !real.realEnabled || real.status?.source !== "raspberry" || real.safety.armed !== true || real.safety.landed_state === 1}
                    rtlDisabled={!rtlG.allowed || !!real.criticalAction || real.conn !== "open" || !real.realEnabled || real.status?.source !== "raspberry" || real.safety.armed !== true || real.safety.landed_state === 1}
                    onAction={(a) => pilot.touchAction(a)} />
                )}
              </Draggable>
              <Draggable pos={layout.camera} defaultClass="right-3 bottom-3" boundsRef={cockpitRef} onChange={(p) => setBlock("camera", p)}>
                {(hp) => <CameraJoystick zoomOnly realZoom={videoEnabled} disabled={!videoEnabled}
                  digitalZoom={camZoom.level} onZoomIn={camZoom.zoomIn} onZoomOut={camZoom.zoomOut} onZoomReset={camZoom.reset} zoomApplying={camZoom.applying} handleProps={hp} />}
              </Draggable>
            </div>
          </div>

          {/* Astuce mobile : piloter en plein écran paysage */}
          <button data-testid="mobile-fs-hint" onClick={toggleFullscreen}
            className="sm:hidden w-full flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#38BDF8] bg-[#38BDF8]/10 border border-[#38BDF8]/30 rounded-sm px-3 py-2">
            <Maximize2 size={13} /> Plein écran (paysage) pour piloter
          </button>

          {/* Telemetry strip — RÉEL depuis realHud (jamais de simulation) */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <Field label="Altitude" value={realHud.alt == null ? "—" : num(realHud.alt, 1)} unit="m" />
            <Field label="Vitesse" value={realHud.speed == null ? "—" : num(realHud.speed, 1)} unit="m/s" />
            <Field label="Cap" value={realHud.heading == null ? "—" : num(realHud.heading, 0)} unit="°" />
            <Field label="Batterie" value={realHud.battery == null ? "—" : num(realHud.battery, 0)} unit="%" color={(realHud.battery ?? 100) < 25 ? "#EF4444" : "#22C55E"} />
            <Field label="Zoom" value={videoEnabled ? num(camZoom.level, 1) : "—"} unit="x" color="#2DD4BF" />
            <Field label="GPS" value={realHud.gps == null ? "—" : realHud.gps ? "FIX" : "NO FIX"} color={realHud.gps ? "#22C55E" : "#F59E0B"} />
          </div>

          {/* Contrôles vidéo (badges, VLC, activation) — la vidéo elle-même est dans le cockpit ci-dessus */}
          {videoEnabled && (
            <div data-testid="cockpit-video-controls" className="flex flex-wrap items-center gap-1.5">
              <span data-testid="video-status-pill" className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider" style={{ borderColor: vui.color + "55", color: vui.color }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: vui.color }} /> {vui.label}
              </span>
              <VBadge on={videoActive} icon={Cctv} label="Caméra active" testid="badge-camera" />
              <VBadge on={!!vf.health?.checks?.rtsp?.reachable} icon={Radio} label="RTSP" testid="badge-rtsp" />
              <VBadge on={vf.playMode === "webrtc" || !!vf.health?.checks?.webrtc?.reachable} icon={Wifi} label="WebRTC" testid="badge-webrtc" />
              <VBadge on={vf.health?.latency_ms != null} icon={Gauge} label="Latence" value={vf.health?.latency_ms != null ? `${vf.health.latency_ms} ms` : null} testid="badge-latency" />
              {vf.health?.raspberry_temp != null && <VBadge on icon={Gauge} label="Temp. RPi" value={`${vf.health.raspberry_temp}°C`} testid="badge-temp" />}
              <button data-testid="video-activate-bar" onClick={() => vf.controlService("start")} disabled={vf.connecting || vf.service.pending}
                className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider border border-[#22C55E]/40 text-[#22C55E] rounded-full px-3 py-1 hover:bg-[#22C55E]/10 disabled:opacity-40">
                <Power size={11} /> Activer vidéo
              </button>
            </div>
          )}

          {/* Santé embarquée Raspberry + Santé flux vidéo */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <EmbeddedHealthPanel health={rpi.health} loading={rpi.loading} error={rpi.error} />
            {videoEnabled && <VideoHealthPanel vf={vf} />}
          </div>

          {/* Connexion réseau du Raspberry (mode 4G/Wi-Fi, signal, VPN) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <NetworkStatusWidget droneId={droneId} variant="panel" netData={net.net} />
          </div>
        </div>

        {/* Right: operator cockpit + real control panel + map + log */}
        <div className="min-w-0 space-y-4">
          <OperatorCockpitPanel real={real} droneId={droneId} readyForLaunchSequence={readyForLaunchSequence} profileLimits={pl} altValid={altCheck.valid} telemetryFresh={!isTelemetryStale(drone?.last_seen)} drone={drone} cockpit={cockpit} />
          <FlightControllerPanel fc={cockpit.flightController} />
          <OperatorFlightSessionPanel droneId={droneId} sessionId={real.sessionId} fs={cockpit.flightSession} />
          <div data-testid="real-panel" className="bg-[#141414] border border-red-500/30 rounded-sm">
            <div className="px-3 py-2 flex items-center gap-2 border-b border-red-500/20">
              <Radio size={12} className="text-red-400" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-red-400">Contrôle réel OFFBOARD</span>
              <span data-testid="real-conn" className="ml-auto font-mono text-[9px] text-white/40">{real.conn}</span>
            </div>
            <div className="p-3 space-y-3">
              {real.banner && (
                <div data-testid="real-panel-banner" className="rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 px-3 py-1.5 text-[10px] flex items-center gap-1.5">
                  <ShieldAlert size={12} /> {real.banner}
                </div>
              )}
              {!controlOpen ? (
                <div data-testid="preflight-hint" className="rounded-sm border border-white/15 bg-white/5 text-white/60 px-3 py-2 text-[10px]">
                  Démarrez le contrôle (bouton « Démarrer » du cockpit) pour charger les préchecks.
                </div>
              ) : (
                !real.status ? (
                  <div data-testid="real-waiting" className="rounded-sm border border-white/15 bg-white/5 text-white/60 px-3 py-1.5 text-[10px]">
                    Attente du statut autoritaire Raspberry…
                  </div>
                ) : !real.realEnabled ? (
                  <div data-testid="real-blocked" className="rounded-sm border border-red-500/40 bg-red-500/10 text-red-300 px-3 py-1.5 text-[10px]">
                    Bloqué : requiert source=raspberry, real_enabled=true, dry_run=false (autorité Pi).
                  </div>
                ) : null
              )}
              {controlOpen && (
                <div data-testid="preflight-panel" className="rounded-sm border border-white/10 bg-white/[0.02] px-3 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] uppercase tracking-widest text-white/50">Préparation au vol</span>
                    <span data-testid="preflight-phase" className="ml-auto font-mono text-[9px] text-white/40">{real.orch?.phase || real.phase}</span>
                  </div>
                  {real.sessionId && <div data-testid="preflight-sid" className="font-mono text-[9px] text-white/40 mb-1">SID {real.sessionId.slice(0, 8)}</div>}
                  <div className="grid grid-cols-1 gap-0">
                    <PfRow label="Backend WS" ok={controlOpen} testid="pf-backend-ws" />
                    <PfRow label="Lease contrôle" ok={!!real.sessionId} testid="pf-lease" />
                    <PfRow label="Gateway relay" ok={gwForwarded} testid="pf-gateway" />
                    <PfRow label="Raspberry authority" ok={real.status?.source === "raspberry" ? real.realEnabled : (real.status ? false : null)} testid="pf-raspberry" />
                    <PfRow label="Flight Controller" ok={fcBlocked ? false : (drone?.flight_controller ? true : null)} testid="pf-fc" />
                    <PfRow label="MAVLink" ok={pfCheck(real.preflight?.checks?.mavlink_connected)} testid="pf-mavlink" />
                    <PfRow label="Télémétrie fraîche" ok={pfCheck(real.preflight?.checks?.telemetry_fresh)} testid="pf-telemetry" />
                    <PfRow label="Drone désarmé" ok={pfCheck(real.preflight?.checks?.disarmed)} testid="pf-disarmed" />
                    <PfRow label="Drone au sol" ok={pfCheck(real.preflight?.checks?.on_ground)} testid="pf-ground" />
                  </div>
                  {fcBlocked && <div data-testid="pf-fc-reason" className="text-[9px] text-red-300 mt-1">Flight Controller : {fcBlocked}</div>}
                  {Array.isArray(real.status?.preconditions?.reasons) && real.status.preconditions.reasons.length > 0 && (
                    <div data-testid="pf-preconditions" className="mt-1 flex flex-wrap gap-1">
                      {real.status.preconditions.reasons.map((r) => (
                        <span key={r} className="rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 px-1.5 py-0.5 text-[9px]">{PRECHECK_LABEL[r] || r}</span>
                      ))}
                    </div>
                  )}
                  <div data-testid="speed-display" className="mt-1.5 flex items-center justify-between gap-2 border-t border-white/5 pt-1.5">
                    <span className="text-[9px] uppercase tracking-wider text-white/50">Vitesse</span>
                    {real.controlLimitsReady && pl ? (
                      <span className="font-mono text-[10px] text-[#3B82F6]" data-testid="speed-limits">
                        {real.speedProfile.toUpperCase()} · XY {pl.max_xy.toFixed(2)} · Z {pl.max_z.toFixed(2)} m/s · YAW {Math.round(pl.max_yaw_deg)}°/s
                      </span>
                    ) : (
                      <span className="font-mono text-[10px] text-[#F59E0B]" data-testid="speed-limits-pending">LIMITES AGENT EN ATTENTE</span>
                    )}
                  </div>
                  {real.takeoffPolicyMismatch && (
                    <div data-testid="takeoff-policy-mismatch" className="mt-1 text-[9px] text-red-300">
                      POLITIQUE TAKEOFF INCOHÉRENTE — Backend {real.backendTakeoffPolicy?.min}–{real.backendTakeoffPolicy?.max} m · Agent {real.agentTakeoffPolicy?.min}–{real.agentTakeoffPolicy?.max} m
                    </div>
                  )}
                  {!real.agentTakeoffPolicy && !real.takeoffPolicyMismatch && (
                    <div data-testid="takeoff-policy-pending" className="mt-1 text-[9px] text-[#F59E0B]">POLITIQUE AGENT EN ATTENTE</div>
                  )}
                  <div data-testid="preflight-prepare-note" className="text-[9px] text-white/35 mt-1.5 leading-relaxed">
                    Validation finale GPS / HOME / batterie : effectuée par PREPARE avant ARM.
                  </div>
                </div>
              )}
              {real.criticalAction && (
                <div data-testid="real-critical-status" className="rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-200 px-3 py-2 text-[10px] font-mono">
                  <div className="uppercase tracking-wider">{real.criticalAction.type} · {real.criticalAction.status}</div>
                  {real.criticalAction.note && <div className="text-amber-300/80 mt-0.5">{real.criticalAction.note}</div>}
                  {real.criticalAction.uncertain && <div data-testid="real-critical-uncertain" className="text-red-300 mt-0.5">Résultat incertain — aucune nouvelle commande envoyée</div>}
                </div>
              )}
              {real.conn === "open" && (
                <p className="text-[9px] text-white/40 leading-relaxed">
                  Commandes ARM+TAKEOFF / Hover / Land / RTL / axes dans le panneau cockpit « Drone · Altitude / Yaw ».
                </p>
              )}
              {/* État autoritaire (lecture seule) — sécurité depuis status.safety_snapshot */}
              <div data-testid="real-state" className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1">
                {[
                  ["Source", real.status?.source, real.status?.source === "raspberry", "real-st-source"],
                  ["Offboard", real.status?.state, real.status?.state === "ACTIVE", "real-st-state"],
                  ["Orchestration", real.orch?.phase, undefined, "real-st-orch"],
                  ["PX4 mode", real.status?.px4_mode ?? real.safety.px4_mode, (real.status?.px4_mode ?? real.safety.px4_mode) === "OFFBOARD", "real-st-px4"],
                  ["Armed", real.safety.armed == null ? "—" : String(real.safety.armed), real.safety.armed === true, "real-st-armed"],
                  ["Landed", real.safety.landed_state, undefined, "real-st-landed"],
                  ["Battery", real.safety.battery_remaining == null ? "—" : `${real.safety.battery_remaining}%`, undefined, "real-st-battery"],
                  ["GPS fix", real.safety.gps_fix_type, (real.safety.gps_fix_type ?? 0) >= 3, "real-st-gps"],
                  ["Sats", real.safety.satellites_used, undefined, "real-st-sats"],
                  ["HDOP", real.safety.hdop, undefined, "real-st-hdop"],
                  ["real_enabled", String(real.status?.real_enabled ?? "—"), real.status?.real_enabled === true, "real-st-realenabled"],
                  ["dry_run", String(real.status?.dry_run ?? "—"), real.status?.dry_run === false, "real-st-dryrun"],
                  ["Local pos", real.safety.local_position_valid == null ? "—" : String(real.safety.local_position_valid), real.safety.local_position_valid === true, "real-st-localpos"],
                  ["Local vel", real.safety.local_velocity_valid == null ? "—" : String(real.safety.local_velocity_valid), real.safety.local_velocity_valid === true, "real-st-localvel"],
                  ["Global pos", real.safety.global_position_valid == null ? "—" : String(real.safety.global_position_valid), real.safety.global_position_valid === true, "real-st-globalpos"],
                  ["Mouvement", real.moveAllowed ? "autorisé" : "verrouillé", real.moveAllowed, "real-st-movement"],
                ].map(([label, value, ok, tid]) => (
                  <div key={tid} className="flex items-center justify-between gap-2 py-0.5 border-b border-white/5">
                    <span className="text-[9px] uppercase tracking-wider text-white/40">{label}</span>
                    <span data-testid={tid} className="font-mono text-[10px]" style={{ color: ok === true ? "#22C55E" : ok === false ? "#EF4444" : "#E5E5E5" }}>{value ?? "—"}</span>
                  </div>
                ))}
              </div>
              {/* Observabilité du stability gate (le Raspberry reste seul décisionnaire) */}
              {(() => {
                const stab = real.status?.stability || real.orch?.stability;
                if (!stab) return null;
                const over = (v, max) => (v != null && v > max);
                const cell = (v, bad) => ({ color: v == null ? "#E5E5E5" : bad ? "#F59E0B" : "#22C55E" });
                return (
                  <div data-testid="real-stability" className="rounded-sm border border-white/10 bg-white/[0.02] px-3 py-2 mt-1">
                    <div className="text-[9px] uppercase tracking-widest text-white/50 mb-1">Stabilité OFFBOARD</div>
                    {real.orch?.phase === "STABILIZING" && <div data-testid="real-stabilizing" className="text-[10px] text-amber-300 mb-1">Stabilisation avant OFFBOARD…</div>}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-[10px]">
                      <div className="flex justify-between"><span className="text-white/40">XY speed</span><span data-testid="stab-xy" style={cell(stab.xy_speed_m_s, over(stab.xy_speed_m_s, 0.30))}>{stab.xy_speed_m_s ?? "—"} / 0.30</span></div>
                      <div className="flex justify-between"><span className="text-white/40">Vz</span><span data-testid="stab-vz" style={cell(stab.vz_m_s, over(stab.vz_m_s, 0.20))}>{stab.vz_m_s ?? "—"} / 0.20</span></div>
                      <div className="flex justify-between"><span className="text-white/40">sample age</span><span data-testid="stab-age" style={cell(stab.sample_age_s, over(stab.sample_age_s, 0.50))}>{stab.sample_age_s ?? "—"} / 0.50</span></div>
                      <div className="flex justify-between"><span className="text-white/40">sample fresh</span><span data-testid="stab-fresh" style={{ color: stab.sample_fresh === false ? "#EF4444" : stab.sample_fresh === true ? "#22C55E" : "#E5E5E5" }}>{String(stab.sample_fresh ?? "—")}</span></div>
                      <div className="flex justify-between"><span className="text-white/40">stable</span><span data-testid="stab-stable" style={{ color: stab.stable === true ? "#22C55E" : stab.stable === false ? "#F59E0B" : "#E5E5E5" }}>{String(stab.stable ?? "—")}</span></div>
                      <div className="flex justify-between"><span className="text-white/40">stable for</span><span data-testid="stab-for">{stab.stable_for_s ?? "—"} / {stab.required_s ?? 1.5} s</span></div>
                    </div>
                    {stab.sample_fresh === false && <div data-testid="stab-stale" className="text-[9px] text-red-300 mt-1">LOCAL_POSITION_NED trop ancien</div>}
                  </div>
                );
              })()}
              <p className="text-[9px] text-white/30 text-center" data-testid="real-axes-note">{real.controlLimitsReady && pl ? `4 axes — XY ${pl.max_xy.toFixed(2)} · Z ${pl.max_z.toFixed(2)} m/s · Yaw ${Math.round(pl.max_yaw_deg)}°/s` : "4 axes — limites Agent en attente"}. La radiocommande reste prioritaire.</p>
            </div>
          </div>

          <div className="bg-[#141414] border border-white/10 rounded-sm overflow-hidden">
            <div className="px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] border-b border-white/10">Carte synchronisée</div>
            <div className="h-72">
              <MapView drones={[mapDrone]} recenter={recenter} realPath={null} basePoint={configuredBasePoint} showHealth
                homePoint={homePoint}
                homeLink={homePoint && mapDrone.positionKnown ? [[mapDrone.lat, mapDrone.lng], [homePoint.lat, homePoint.lng]] : null} />
            </div>
          </div>

          <div className="bg-[#141414] border border-white/10 rounded-sm">
            <div className="px-3 py-2 flex items-center gap-2 border-b border-white/10">
              <ScrollText size={12} className="text-[#A3A3A3]" />
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">Journal commandes manuelles</span>
              <button data-testid="reload-events" onClick={loadEvents} className="ml-auto font-mono text-[9px] uppercase text-[#525252] hover:text-white">↻</button>
            </div>
            <div data-testid="manual-events" className="max-h-72 overflow-y-auto p-2 space-y-1">
              {events.length === 0 && <div className="font-mono text-[10px] text-[#525252] p-2">Aucune action enregistrée</div>}
              {events.map((e) => (
                <div key={e.id} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-1 font-mono text-[10px]">
                  <span className="text-[#525252]">{fmtTime(e.created_at)}</span>
                  <span className={e.action?.startsWith("camera") ? "text-[#2DD4BF] uppercase" : "text-[#3B82F6] uppercase"}>{ACTION_LABEL[e.action] || e.action}</span>
                  <span className="text-[#525252] ml-auto uppercase text-[9px]">{e.source}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ControllerConfig open={configOpen} onClose={() => setConfigOpen(false)} profile={profile} isAdmin={isAdmin} gamepadState={padState}
        onSaved={() => api.get("/controller-profiles/default").then(({ data }) => setProfile(data.profile)).catch(() => {})} />
    </div>
  );
}
